import { useRef, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { api, errMsg, compressImage, waUrl } from "../lib/api";
import { useApp, useData } from "../lib/app";
import Review from "../components/Review";
import { AiChip, AiLoader, Chips, Empty, ErrorCard, Header, Sheet } from "../components/bits";
import { useT } from "../lib/i18n";

const q = encodeURIComponent;
const SHOPS = [["Blinkit", (i) => `https://blinkit.com/s/?q=${q(i)}`], ["Zepto", (i) => `https://www.zeptonow.com/search?query=${q(i)}`], ["Instamart", (i) => `https://www.swiggy.com/instamart/search?query=${q(i)}`]];

function defaultFilters(home, members) {
  const f = home.food || {};
  const out = [{ veg: "Veg", nonveg: "Non-veg", egg: "Eggetarian", jain: "Jain" }[f.diet] || "Veg", "Under 30 min"];
  if (f.elders_soft) { const el = members.find((m) => m.elder); out.push(`Less spicy for ${el?.name || "elders"}`); }
  (f.dislikes || []).forEach((d) => d.item && out.push(`No ${d.item} for ${d.person}`));
  return out;
}

function Cook({ onReview }) {
  const { me } = useApp();
  const qc = useQueryClient();
  const [ings, setIngs] = useState([]);
  const [add, setAdd] = useState("");
  const [allF] = useState(() => defaultFilters(me.home, me.members));
  const [filters, setFilters] = useState(allF);
  const [dishes, setDishes] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const { data: plan, refetch: refetchPlan } = useData("/meals/plan");
  const file = useRef();
  const t = useT();
  const run = async (kind, fn) => { setBusy(kind); setErr(null); try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(null); } };
  const snap = (f) => f && run("meals", async () => {
    const { data } = await api.post("/ai/fridge", { image: await compressImage(f) });
    if (data.unclear) throw new Error(); setIngs(data.ingredients || []);
  }).catch(() => setErr("Photo not clear. Please try again in good light."));
  const suggest = () => run("meals", async () => setDishes((await api.post("/ai/dishes", { ingredients: ings, filters })).data.dishes || []));
  const week = () => run("plan", async () => { await api.post("/ai/weekplan", { filters, ingredients: ings }); refetchPlan(); });
  const toList = async (items) => {
    try { const { data } = await api.post("/grocery", { items: items.map((name) => ({ name })) }); toast.success(t("{n} items added to the grocery list 🛒", { n: data.length })); qc.invalidateQueries({ queryKey: ["/grocery"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const assign = (dish) => run("plan", async () => onReview((await api.post("/meals/assign", { dish })).data));
  const editDay = (i, k, v) => { const days = plan.days.map((d, j) => (j === i ? { ...d, [k]: v } : d)); api.put("/meals/plan", { ...plan, days }).then(() => refetchPlan()); };

  return (
    <div className="space-y-4">
      <button data-testid="fridge-snap-btn" onClick={() => file.current.click()} className="ul-card w-full p-4 flex items-center gap-4 text-left">
        <span className="text-4xl w-16 h-16 rounded-2xl bg-[#DFF6F3] flex items-center justify-center">📷</span>
        <span><b className="font-display text-lg block">{t("Snap your fridge or vegetable basket")}</b><span className="text-sm text-[#6B6680]">{t("AI lists what it can see")}</span></span>
      </button>
      <input ref={file} type="file" accept="image/*" capture="environment" hidden data-testid="fridge-file-input" onChange={(e) => snap(e.target.files[0])} />
      {busy === "meals" && <AiLoader kind="meals" />}
      {err && <ErrorCard text={err} onRetry={() => setErr(null)} />}
      <div className="ul-card p-4 space-y-3">
        <div className="flex items-center justify-between"><p className="ul-label">{t("What you have")}</p>{ings.length > 0 && <AiChip text="AI found in your fridge" />}</div>
        <div className="flex flex-wrap gap-2" data-testid="ingredient-chips">
          {ings.map((i) => <button key={i} className="ul-chip" data-on="true" onClick={() => setIngs(ings.filter((x) => x !== i))}>{i} ×</button>)}
        </div>
        <div className="flex gap-2">
          <input data-testid="ingredient-add-input" className="ul-input" placeholder={t("Add an ingredient")} value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && add.trim()) { setIngs([...ings, add.trim()]); setAdd(""); } }} />
          <button className="ul-btn ul-ghost" onClick={() => { if (add.trim()) { setIngs([...ings, add.trim()]); setAdd(""); } }}>{t("Add")}</button>
        </div>
        <p className="ul-label">{t("Filters")}</p>
        <Chips options={allF} value={filters} onChange={setFilters} testid="meal-filter" />
        <div className="flex gap-2">
          <button data-testid="suggest-dishes-btn" disabled={!ings.length || !!busy} onClick={suggest} className="ul-btn ul-primary flex-1">{t("✨ What to cook?")}</button>
          <button data-testid="plan-week-btn" disabled={!!busy} onClick={week} className="ul-btn ul-ghost">{t("Plan my week")}</button>
        </div>
      </div>
      {dishes && dishes.map((d, i) => (
        <div key={i} className="ul-card p-4 space-y-2" data-testid={`dish-card-${i}`}>
          <div className="flex justify-between"><b className="font-display text-lg">{d.name}</b><span className="text-sm text-[#6B6680]">{d.minutes} min</span></div>
          <p className="text-sm text-[#0F8C80]">{t("Uses what you have")}: {(d.uses || []).join(", ")}</p>
          {d.missing?.length > 0 && <p className="text-sm text-[#6B6680]">{t("Missing")}: {d.missing.join(", ")}</p>}
          <div className="flex flex-wrap gap-2">
            <a data-testid={`dish-recipe-${i}`} href={`https://www.youtube.com/results?search_query=${q(d.name + " recipe")}`} target="_blank" rel="noreferrer" className="ul-btn ul-ghost flex-1">{t("▶ Watch recipe")}</a>
            {d.missing?.length > 0 && <button className="ul-btn ul-ghost" onClick={() => toList(d.missing)}>{t("+ Grocery")}</button>}
            <button data-testid={`dish-assign-${i}`} className="ul-btn ul-primary" onClick={() => assign(d.name)}>{t("Assign prep")}</button>
          </div>
        </div>
      ))}
      {busy === "plan" && <AiLoader kind="plan" />}
      {plan?.days?.length > 0 && (
        <div className="ul-card p-4" data-testid="week-plan">
          <div className="flex justify-between items-center mb-2"><p className="ul-label">{t("This week's meals")}</p><AiChip /></div>
          {plan.days.map((d, i) => (
            <div key={i} className="py-2 border-b border-[#F3F0FF] last:border-0">
              <p className="text-xs font-extrabold text-[#6A5AE0]">{d.day}</p>
              <textarea rows={1} className="w-full text-sm outline-none resize-none [field-sizing:content]" value={d.lunch} onChange={(e) => editDay(i, "lunch", e.target.value)} aria-label="lunch" />
              <textarea rows={1} className="w-full text-sm outline-none resize-none text-[#6B6680] [field-sizing:content]" value={d.dinner} onChange={(e) => editDay(i, "dinner", e.target.value)} aria-label="dinner" />
            </div>
          ))}
          {plan.missing?.length > 0 && <button data-testid="make-grocery-btn" className="ul-btn ul-primary w-full mt-3" onClick={() => toList(plan.missing)}>{t("Make grocery list ({n})", { n: plan.missing.length })}</button>}
        </div>
      )}
    </div>
  );
}

function Grocery() {
  const { me } = useApp();
  const { data, refetch, isLoading } = useData("/grocery");
  const [add, setAdd] = useState("");
  const t = useT();
  const adults = me.members.filter((m) => m.type !== "child" && m.status !== "pending");
  const patch = async (id, body) => { await api.patch(`/grocery/${id}`, body); refetch(); };
  const create = async () => { if (!add.trim()) return; await api.post("/grocery", { items: [{ name: add.trim() }] }); setAdd(""); refetch(); };
  const share = () => {
    const open = (data || []).filter((g) => !g.done);
    return waUrl(`🛒 Grocery list from ${me.home.name}\n` + open.map((g) => `• ${g.name}${g.original ? ` (${g.original})` : ""}${g.qty ? ` – ${g.qty}` : ""}`).join("\n"));
  };
  if (isLoading) return <AiLoader />;
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input data-testid="grocery-add-input" className="ul-input" placeholder={t("Add an item")} value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} />
        <button data-testid="grocery-add-btn" className="ul-btn ul-primary" onClick={create}>{t("Add")}</button>
      </div>
      {!data?.length ? <Empty emoji="🧺" text="Your grocery list is empty. Scan a list or plan meals to fill it." /> : data.map((g) => (
        <div key={g.id} className={`ul-card p-3 ${g.done ? "opacity-60" : ""}`} data-testid={`grocery-item-${g.id}`}>
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={g.done} onChange={() => patch(g.id, { done: !g.done })} className="w-6 h-6 accent-[#6A5AE0]" data-testid="grocery-check" />
            <p className={`flex-1 font-bold ${g.done ? "line-through" : ""}`}>{g.name}{g.original ? <span className="font-normal text-[#6B6680]"> ({g.original})</span> : ""}{g.qty ? <span className="font-normal"> – {g.qty}</span> : ""}</p>
            <select className="text-xs font-bold bg-[#F3F0FF] rounded-full px-2 py-1" value={g.assignee_id || ""} onChange={(e) => patch(g.id, { assignee_id: e.target.value || null })} aria-label="assign">
              <option value="">{t("Anyone")}</option>{adults.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button onClick={async () => { await api.delete(`/grocery/${g.id}`); refetch(); }} className="text-[#9C97AE]" aria-label="remove"><Trash2 size={16} /></button>
          </div>
          {!g.done && <div className="flex gap-2 mt-2 pl-9">{SHOPS.map(([n, u]) => <a key={n} href={u(g.name)} target="_blank" rel="noreferrer" className="text-xs font-extrabold px-3 py-1.5 rounded-lg bg-[#F7F5FE] text-[#5B4BD6]">{n}</a>)}</div>}
        </div>
      ))}
      {data?.some((g) => !g.done) && <a data-testid="grocery-share-wa" href={share()} target="_blank" rel="noreferrer" className="ul-btn ul-wa w-full">{t("Share list on WhatsApp")}</a>}
      <p className="text-xs text-center text-[#6B6680]">{t("Shop buttons are simple search links. We are not connected to these apps.")}</p>
    </div>
  );
}

export default function Meals() {
  const [tab, setTab] = useState("cook");
  const [review, setReview] = useState(null);
  const t = useT();
  return (
    <div>
      <Header title="What to cook today?" kicker="Meals & groceries" />
      <div className="flex bg-[#F1EEFB] rounded-2xl p-1 mb-4">
        {[["cook", "🍲 Cook"], ["grocery", "🛒 Grocery list"]].map(([k, l]) => (
          <button key={k} data-testid={`meals-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 min-h-[44px] rounded-xl font-extrabold text-sm ${tab === k ? "bg-white text-[#6A5AE0] shadow" : "text-[#6B6680]"}`}>{t(l)}</button>
        ))}
      </div>
      {tab === "cook" ? <Cook onReview={setReview} /> : <Grocery />}
      <Sheet open={!!review} onClose={() => setReview(null)} testid="meal-review-sheet">
        {review && <Review result={review} onSaved={() => setReview(null)} />}
      </Sheet>
    </div>
  );
}
